import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permissionsAPI } from '../utils/api';

const DEFAULT_MATRIX = [
  { area: 'Tableau de bord', action: 'Consulter', admin: true, manager: true, staff: true, usager: true },
  { area: 'Utilisateurs', action: 'Créer / modifier / désactiver', admin: true, manager: false, staff: false, usager: false },
  { area: 'Centres', action: 'Créer / modifier / supprimer', admin: true, manager: false, staff: false, usager: false },
  { area: 'Horaires', action: 'Créer / modifier / supprimer', admin: true, manager: true, staff: false, usager: false },
  { area: 'Congés', action: 'Approuver / rejeter', admin: true, manager: true, staff: false, usager: false },
  { area: 'Pointage', action: 'Pointer entrée / sortie', admin: true, manager: true, staff: true, usager: true },
  { area: 'Usagers', action: 'Consulter fiche', admin: true, manager: true, staff: false, usager: true },
  { area: 'Usagers', action: 'Modifier dossier de soins', admin: true, manager: true, staff: false, usager: false },
  { area: 'Assignations PAB', action: 'Créer / terminer', admin: true, manager: true, staff: false, usager: false },
  { area: 'Activités', action: 'Créer / modifier activité', admin: true, manager: true, staff: false, usager: false },
  { area: 'Activités', action: 'Gérer le catalogue (titres/catégories)', admin: true, manager: false, staff: false, usager: false },
  { area: 'Activités', action: 'Inscrire usager', admin: true, manager: true, staff: false, usager: true },
  { area: 'Activités', action: 'Marquer présence/absence', admin: true, manager: true, staff: true, usager: false },
  { area: 'Transport', action: 'Créer trajet', admin: true, manager: true, staff: false, usager: false },
  { area: 'Transport', action: 'Mettre à jour statut trajet', admin: true, manager: true, staff: true, usager: false },
  { area: 'Incidents/Accidents', action: 'Déclarer un incident ou un accident', admin: true, manager: true, staff: true, usager: false },
  { area: 'Incidents', action: 'Résoudre / fermer incident', admin: true, manager: true, staff: true, usager: false },
  { area: 'Rapports', action: 'Consulter rapports opérationnels', admin: true, manager: true, staff: false, usager: false },
  { area: 'Matrice permissions', action: 'Consulter', admin: true, manager: false, staff: false, usager: false },
];

const RolesCell = ({ checked, onChange }) => (
  <input type="checkbox" checked={!!checked} onChange={onChange} />
);

const translatePermissionArea = (area, tr) => {
  const map = {
    'Tableau de bord': tr('Tableau de bord', 'Dashboard'),
    Utilisateurs: tr('Utilisateurs', 'Users'),
    Centres: tr('Centres', 'Centers'),
    Horaires: tr('Horaires', 'Schedules'),
    'Congés': tr('Congés', 'Leaves'),
    Pointage: tr('Pointage', 'Time clock'),
    Usagers: tr('Usagers', 'Patients'),
    'Assignations PAB': tr('Assignations PAB', 'PAB assignments'),
    'Activités': tr('Activités', 'Activities'),
    Transport: tr('Transport', 'Transport'),
    'Incidents/Accidents': tr('Incidents/Accidents', 'Incidents/Accidents'),
    Incidents: tr('Incidents', 'Incidents'),
    'Confidentialité': tr('Confidentialité', 'Privacy'),
    Rapports: tr('Rapports', 'Reports'),
    'Matrice permissions': tr('Matrice permissions', 'Permissions matrix'),
  };
  return map[area] || area;
};

const translatePermissionAction = (action, tr) => {
  const map = {
    Consulter: tr('Consulter', 'View'),
    'Créer / modifier / désactiver': tr('Créer / modifier / désactiver', 'Create / edit / disable'),
    'Créer / modifier / supprimer': tr('Créer / modifier / supprimer', 'Create / edit / delete'),
    'Approuver / rejeter': tr('Approuver / rejeter', 'Approve / reject'),
    'Pointer entrée / sortie': tr('Pointer entrée / sortie', 'Clock in / out'),
    'Consulter fiche': tr('Consulter fiche', 'View profile'),
    'Modifier dossier de soins': tr('Modifier dossier de soins', 'Edit care record'),
    'Créer / terminer': tr('Créer / terminer', 'Create / end'),
    'Créer / modifier activité': tr('Créer / modifier activité', 'Create / edit activity'),
    'Gérer le catalogue (titres/catégories)': tr('Gérer le catalogue (titres/catégories)', 'Manage catalog (titles/categories)'),
    'Inscrire usager': tr('Inscrire usager', 'Enroll patient'),
    'Marquer présence/absence': tr('Marquer présence/absence', 'Mark attendance/absence'),
    'Créer trajet': tr('Créer trajet', 'Create trip'),
    'Mettre à jour statut trajet': tr('Mettre à jour statut trajet', 'Update trip status'),
    'Déclarer un incident ou un accident': tr('Déclarer un incident ou un accident', 'Report an incident or accident'),
    'Résoudre / fermer incident': tr('Résoudre / fermer incident', 'Resolve / close incident'),
    'Tenir le registre des incidents de confidentialité': tr('Tenir le registre des incidents de confidentialité', 'Maintain the privacy incident register'),
    'Consulter rapports opérationnels': tr('Consulter rapports opérationnels', 'View operational reports'),
  };
  return map[action] || action;
};

const PermissionsPage = () => {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const tr = (fr, en) => (isFr ? fr : en);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await permissionsAPI.getMatrix();
      const items = Array.isArray(res.data?.items) && res.data.items.length ? res.data.items : DEFAULT_MATRIX;
      setMatrix(items);
    } catch (err) {
      console.error(err);
      setMatrix(DEFAULT_MATRIX);
      setError(tr("Impossible de charger la matrice depuis le serveur, affichage des valeurs par défaut.", 'Unable to load matrix from server, showing default values.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateCell = (index, key, value) => {
    setMatrix((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
    setMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await permissionsAPI.updateMatrix(matrix);
      setMessage(tr('Matrice sauvegardée avec succès.', 'Matrix saved successfully.'));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || tr('Erreur lors de la sauvegarde de la matrice.', 'Error while saving matrix.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{tr('Matrice des permissions', 'Permissions matrix')}</h2>
          <p className="text-muted">{tr("Définition des droits par rôle d'accès (modifiable par administrateur)", 'Permission definition by access role (editable by administrator)')}</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? tr('Enregistrement...', 'Saving...') : tr('Enregistrer', 'Save')}
        </button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ paddingBottom: 12 }}>
          <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 8 }}>
            {tr('Rôles applicatifs:', 'Application roles:')}
            <strong> {t('roles.admin')}</strong>,
            <strong> {t('roles.manager')}</strong>,
            <strong> {t('roles.staff')}</strong>,
            <strong> {t('roles.usager')}</strong>.
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            {tr("Les fonctions métier (Médecin, Infirmier, PAB, etc.) se définissent dans le profil utilisateur et n'accordent pas automatiquement de permissions supplémentaires.", 'Job functions (Doctor, Nurse, PAB, etc.) are defined in the user profile and do not automatically grant additional permissions.')}
          </div>
          {message && <div className="alert alert-success" style={{ marginTop: 12 }}>✅ {message}</div>}
          {error && <div className="alert alert-error" style={{ marginTop: 12 }}>⚠ {error}</div>}
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div className="spinner" style={{ margin: 'auto' }} />
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{tr('Domaine', 'Area')}</th>
                  <th>{tr('Action', 'Action')}</th>
                  <th>{t('roles.admin')}</th>
                  <th>{t('roles.manager')}</th>
                  <th>{t('roles.staff')}</th>
                  <th>{t('roles.usager')}</th>
                </tr>
              </thead>
              <tbody>
                {matrix.map((row, idx) => (
                  <tr key={`${row.area}-${row.action}-${idx}`}>
                    <td style={{ fontWeight: 600 }}>{translatePermissionArea(row.area, tr)}</td>
                    <td>{translatePermissionAction(row.action, tr)}</td>
                    <td><RolesCell checked={row.admin} onChange={(e) => updateCell(idx, 'admin', e.target.checked)} /></td>
                    <td><RolesCell checked={row.manager} onChange={(e) => updateCell(idx, 'manager', e.target.checked)} /></td>
                    <td><RolesCell checked={row.staff} onChange={(e) => updateCell(idx, 'staff', e.target.checked)} /></td>
                    <td><RolesCell checked={row.usager} onChange={(e) => updateCell(idx, 'usager', e.target.checked)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
